import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, CalendarDays, Clock, Wallet, AlertTriangle, CheckCircle2, UserPlus, Trash2, ClipboardCheck,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useSession } from '../lib/SessionContext';
import { t, formatMoney, formatTime, formatDate } from '../i18n';
import {
  Card, StatCard, Spinner, EmptyState, Badge, Alert, Button, Input, Select, Field,
} from '../components/ui';
import PatientAdoption from '../components/PatientAdoption';
import PatientCancelModal from '../components/PatientCancelModal';
import PatientCancellationsNotice from '../components/PatientCancellationsNotice';
import PendingForms from '../components/PendingForms';
import { owedAppointmentsQuery, sumOwed } from '../lib/billing';
import { patientMayCancel } from '../lib/cancellation';
import { dayKey, dayRange } from '../lib/practiceTime';

/** Today on the practice's calendar, as ISO instants — not the browser's today. */
const todayRange = () => dayRange(dayKey());

const DELIVERY_CHECKS = [
  ['invitesFailed', 'invites_failed'],
  ['invitesStuck', 'invites_stuck'],
  ['referralsFailed', 'referral_notices_failed'],
  ['referralsStuck', 'referral_notices_stuck'],
  ['billingSummariesFailed', 'billing_summaries_failed'],
  ['billingSummariesStuck', 'billing_summaries_stuck'],
  ['sessionRequestsFailed', 'session_request_notices_failed'],
  ['sessionRequestsStuck', 'session_request_notices_stuck'],
  ['treatmentNoticesFailed', 'treatment_notices_failed'],
  ['treatmentNoticesStuck', 'treatment_notices_stuck'],
  ['remindersLate', 'reminders_late'],
  ['noteRemindersLate', 'note_reminders_late'],
  ['httpErrors', 'http_errors'],
  ['cronFailures', 'cron_failures'],
];

/**
 * Whether reminders and invitations are actually going out.
 *
 * Every way this has failed so far was silent — a wrong secret, an expired SMTP
 * password, a call timing out — because the scheduled jobs report success as
 * soon as they have dispatched a request. delivery_health() (migration 0039)
 * counts what those failures leave behind. Counts only, so it is admin's to see.
 */
const DeliveryHealth = () => {
  const [check, setCheck] = useState(null);

  useEffect(() => {
    supabase.rpc('delivery_health').then(({ data, error }) => setCheck({ health: data, error }));
  }, []);

  if (!check) return null;

  // A check that could not run must not look like one that found nothing: that
  // silence is the very failure this panel exists to end.
  if (check.error || !check.health) {
    return (
      <Alert tone="warning" title={t('delivery.checkFailedTitle')}>
        {t('delivery.checkFailed', { message: check.error?.message ?? '—' })}
      </Alert>
    );
  }

  const problems = DELIVERY_CHECKS
    .map(([label, field]) => [label, Number(check.health[field] ?? 0)])
    .filter(([, count]) => count > 0);

  if (!problems.length) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-slate-500">
        <CheckCircle2 size={13} className="shrink-0 text-emerald-600" />
        {t('delivery.allClear', { time: formatTime(check.health.checked_at) })}
      </p>
    );
  }

  return (
    <Alert tone="warning" title={t('delivery.title')}>
      <ul className="list-inside list-disc">
        {problems.map(([label, count]) => <li key={label}>{t(`delivery.${label}`, { count })}</li>)}
      </ul>
      <p className="mt-2 text-xs">{t('delivery.hint')}</p>
    </Alert>
  );
};

const STATUS_TONE = {
  requested: 'warning',
  scheduled: 'info',
  waiting: 'warning',
  in_session: 'indigo',
  completed: 'success',
  cancelled: 'neutral',
  no_show: 'danger',
};

const ProfessionalHome = ({ therapistId, isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [today, setToday] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { start, end } = todayRange();

    // Every professional's home is their own practice, admin's included:
    // other professionals' patients are not admin's to see (migration 0038).
    // What admin keeps is the practice-wide adoption reading below, which
    // carries no names, and the subscriptions it runs.
    const scoped = (query) => query.eq('therapist_id', therapistId);

    try {
      const [patients, todaysAppointments, pending, sessionRequests, unpaid, overdueSubs] = await Promise.all([
        scoped(supabase.from('treatments').select('id', { count: 'exact', head: true }))
          .eq('status', 'approved'),
        scoped(
          supabase
            .from('appointments')
            .select('id,starts_at,status,dependents(first_name,last_name)')
        ).gte('starts_at', start).lt('starts_at', end).order('starts_at'),
        scoped(supabase.from('treatments').select('id', { count: 'exact', head: true }))
          .eq('status', 'pending'),
        // Sessions asked for in hours that need approval wait there too (0055).
        scoped(supabase.from('appointments').select('id', { count: 'exact', head: true }))
          .eq('status', 'requested'),
        // Debt is what has been delivered and not paid — not everything whose
        // start time has passed. See lib/billing.js.
        owedAppointmentsQuery({ therapistId }),
        // Overdue as Suscripciones shows it: ended and unpaid, on the practice's
        // calendar (0067). Counting every pending period included the month
        // still running, so a new month read as everyone behind.
        isAdmin
          ? supabase.from('subscription_status').select('id', { count: 'exact', head: true })
              .eq('is_overdue', true)
          : Promise.resolve({ count: 0 }),
      ]);

      const failure = [patients, todaysAppointments, pending, sessionRequests, unpaid, overdueSubs]
        .find((result) => result?.error);
      if (failure) throw failure.error;

      setStats({
        patients: patients.count ?? 0,
        todayCount: todaysAppointments.data?.length ?? 0,
        pending: (pending.count ?? 0) + (sessionRequests.count ?? 0),
        // What those sessions still lack, not their price (0072).
        owed: sumOwed(unpaid.data),
        overdueSubs: overdueSubs.count ?? 0,
      });
      setToday(todaysAppointments.data ?? []);
    } catch (loadError) {
      // Without this the screen would sit on a spinner forever.
      console.error('HomeView load failed', loadError);
      setError(loadError.message ?? String(loadError));
    } finally {
      setLoading(false);
    }
  }, [therapistId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;
  if (error) return <Alert tone="danger" title={t("home.loadFailed")}>{error}</Alert>;

  return (
    <div className="space-y-6">
      {isAdmin && <DeliveryHealth />}

      {isAdmin && stats.overdueSubs > 0 && (
        <Alert tone="warning" title={t('subscriptions.title')}>
          {t('subscriptions.overdueAlert', { count: stats.overdueSubs })}{' '}
          <Link to="/suscripciones" className="font-semibold underline">{t('common.view')}</Link>
        </Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Users} tone="emerald" label={t('nav.patients')} value={stats.patients} />
        <StatCard icon={CalendarDays} tone="indigo" label={t('home.todayAppointments')} value={stats.todayCount} />
        {/* Opens Solicitudes: a count is most useful next to where it is answered. */}
        <Link to="/solicitudes" className="block rounded-xl transition hover:opacity-90">
          <StatCard icon={Clock} tone="amber" label={t('nav.approvals')} value={stats.pending} />
        </Link>
        <StatCard icon={Wallet} tone="rose" label={t('cashFlow.owed')} value={formatMoney(stats.owed)} />
      </div>

      {/* A button as well as the card: a coloured tile does not read as
          something to press. */}
      {stats.pending > 0 && (
        <Alert tone="warning">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{t('home.pendingRequests', { count: stats.pending })}</span>
            <Link to="/solicitudes">
              <Button variant="secondary"><ClipboardCheck size={16} />{t('home.reviewRequests')}</Button>
            </Link>
          </div>
        </Alert>
      )}

      {/* Cancellations and withdrawn requests from patients since the
          professional last pressed "Entendido" (0059). */}
      <PatientCancellationsNotice />

      {/* Below the day's numbers, above the day's agenda: it is a standing
          reading rather than something to act on this morning. */}
      <PatientAdoption />

      <Card title={t('home.todayAgenda')} subtitle={formatDate(new Date())}>
        {today.length === 0 ? (
          <EmptyState icon={CalendarDays} title={t('appointments.noAppointmentsToday')} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {today.map((appointment) => (
              <li key={appointment.id} className="flex items-center justify-between gap-4 py-3">
                <div className="flex items-center gap-4">
                  <span className="font-mono text-sm font-medium text-slate-900">
                    {formatTime(appointment.starts_at)}
                  </span>
                  <p className="text-sm font-medium text-slate-900">
                    {appointment.dependents?.first_name} {appointment.dependents?.last_name}
                  </p>
                </div>
                <Badge tone={STATUS_TONE[appointment.status]}>
                  {t(`appointments.status.${appointment.status}`)}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

// Stored on the row and shown on the card. Anyone added from here is a minor:
// an adult has their own account (migration 0038).
const MINOR_RELATIONSHIP = 'menor a cargo';

/** Whether a YYYY-MM-DD date of birth is under eighteen today. */
const isMinor = (dateOfBirth) => {
  if (!dateOfBirth) return false;
  const [year, month, day] = dateOfBirth.split('-').map(Number);
  return new Date() < new Date(year + 18, month - 1, day);
};

const EMPTY_MINOR = { firstName: '', lastName: '', dateOfBirth: '', therapistId: '' };

/**
 * Adding a child, or any minor in the holder's care.
 *
 * This is where a professional's refusal points: a patient can no longer be
 * filed under an existing account by email (0038), so the family adds them
 * from the account they already hold, and the professional accepts the request.
 */
// The form only: its button sits in the family card's header, beside the title,
// so the card no longer ends in a button below the list (15 Sep 2026).
const AddMinor = ({ accountId, onAdded, onClose }) => {
  const [professionals, setProfessionals] = useState([]);
  const [form, setForm] = useState(EMPTY_MINOR);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    supabase.rpc('professional_directory').then(({ data }) => setProfessionals(data ?? []));
  }, []);

  const save = async () => {
    setError(null);
    if (!form.firstName.trim() || !form.lastName.trim() || !form.dateOfBirth) {
      setError(t('family.fillRequired'));
      return;
    }
    // Said here so the message names the rule; the database refuses it too.
    if (!isMinor(form.dateOfBirth)) {
      setError(t('family.minorsOnly'));
      return;
    }
    setSaving(true);
    const { data: added, error: insertError } = await supabase.from('dependents').insert([{
      account_id: accountId,
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      date_of_birth: form.dateOfBirth,
      relationship_to_account: MINOR_RELATIONSHIP,
    }]).select('id').single();
    if (insertError) { setSaving(false); setError(insertError.message); return; }

    // The person first, then the request: which professional sees them is a
    // treatment of its own (migration 0040), and they may later see others.
    if (form.therapistId) {
      const { error: requestError } = await supabase.rpc('request_professional', {
        p_dependent_id: added.id, p_therapist_id: form.therapistId,
      });
      if (requestError) { setSaving(false); setError(requestError.message); onAdded(); return; }
    }
    setSaving(false);
    setForm(EMPTY_MINOR);
    onClose();
    onAdded();
  };

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-4">
      <p className="text-sm text-slate-600">{t('family.addMinorHint')}</p>
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('register.firstName')} required>
          <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
        </Field>
        <Field label={t('register.lastName')} required>
          <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
        </Field>
        <Field label={t('patients.dateOfBirth')} required>
          <Input type="date" value={form.dateOfBirth}
            onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
        </Field>
        <Field label={t('patients.assignedProfessional')} hint={t('family.professionalHint')}>
          <Select value={form.therapistId} onChange={(e) => setForm({ ...form, therapistId: e.target.value })}>
            <option value="">{t('register.chooseLater')}</option>
            {professionals.map((row) => (
              <option key={row.id} value={row.id}>
                {row.first_name} {row.last_name}{row.discipline ? ` · ${row.discipline}` : ''}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button onClick={save} loading={saving}>{t('common.save')}</Button>
      </div>
    </div>
  );
};

/**
 * Asking for a professional for someone in the family, alongside any they
 * already see. The professional accepts or declines; they are not told who
 * else the person sees.
 */
const RequestProfessional = ({ dependent, taken, onRequested }) => {
  const [open, setOpen] = useState(false);
  const [professionals, setProfessionals] = useState([]);
  const [therapistId, setTherapistId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    supabase.rpc('professional_directory').then(({ data }) => setProfessionals(data ?? []));
  }, [open]);

  // Nobody they already see, or already asked for.
  const available = professionals.filter((row) => !taken.has(row.id));

  const send = async () => {
    setSaving(true);
    setError(null);
    const { error: requestError } = await supabase.rpc('request_professional', {
      p_dependent_id: dependent.id, p_therapist_id: therapistId,
    });
    setSaving(false);
    if (requestError) { setError(requestError.message); return; }
    const chosen = professionals.find((row) => row.id === therapistId);
    setOpen(false);
    setTherapistId('');
    onRequested(t('family.requestSent', { name: chosen ? `${chosen.first_name} ${chosen.last_name}` : '' }));
  };

  // A button like "Agregar a un menor a tu cargo", not a link: as small blue text
  // under the professional's name it went unnoticed on the first try.
  if (!open) {
    return (
      <Button variant="secondary" className="mt-3" onClick={() => setOpen(true)}>
        <UserPlus size={16} />{t('family.requestProfessional')}
      </Button>
    );
  }

  return (
    <div className="mt-3 space-y-2 rounded-lg bg-slate-50 p-3">
      <p className="text-xs text-slate-600">{t('family.requestProfessionalHint')}</p>
      {error && <Alert tone="danger">{error}</Alert>}
      {professionals.length > 0 && available.length === 0 ? (
        <p className="text-xs text-slate-500">{t('family.noOtherProfessionals')}</p>
      ) : (
        <Select value={therapistId} onChange={(event) => setTherapistId(event.target.value)}>
          <option value="">{t('family.requestPick')}</option>
          {available.map((row) => (
            <option key={row.id} value={row.id}>
              {row.first_name} {row.last_name}{row.discipline ? ` · ${row.discipline}` : ''}
            </option>
          ))}
        </Select>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={() => { setOpen(false); setError(null); }}>{t('common.cancel')}</Button>
        <Button onClick={send} loading={saving} disabled={!therapistId}>{t('family.requestSend')}</Button>
      </div>
    </div>
  );
};

const TREATMENT_BADGE = {
  approved: ['success', CheckCircle2, 'patients.active'],
  pending: ['warning', Clock, 'patients.pendingApproval'],
  declined: ['danger', AlertTriangle, 'patients.declined'],
  revoked: ['neutral', null, 'patients.status.revoked'],
};

const PatientHome = ({ accountId }) => {
  const [loading, setLoading] = useState(true);
  const [dependents, setDependents] = useState([]);
  const [appointments, setAppointments] = useState([]);
  // One line per professional the patient owes, among those who show balances.
  const [owed, setOwed] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // The session whose cancel dialog is open (0057).
  const [cancelling, setCancelling] = useState(null);
  // Whether the form to add a minor is open, from the family card's header.
  const [addingMinor, setAddingMinor] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // Professionals' names come from my_professionals(), not from embedding
    // the therapists table: patients cannot read that table (migration 0035),
    // because its rows also carry contact details, fees and block reasons.
    // Treatments, sessions and balances come from functions for the same kind
    // of reason (0046): they return the patient's own part, not the agreed
    // price, what an obra social pays, or why a payment was voided.
    const [deps, treatments, appts, professionals, balance] = await Promise.all([
      supabase
        .from('dependents')
        .select('id, first_name, last_name, relationship_to_account, insurance_plans(name, insurance_companies(name))')
        .eq('account_id', accountId)
        .order('created_at'),
      // Every professional each of them sees or asked for (migration 0040).
      supabase.rpc('my_treatments'),
      supabase.rpc('my_sessions', { p_from: new Date().toISOString(), p_limit: 5 }),
      supabase.rpc('my_professionals'),
      supabase.rpc('my_balance'),
    ]);
    const byId = Object.fromEntries((professionals.data ?? []).map((row) => [row.id, row]));
    const named = (id) => (byId[id] ? `${byId[id].first_name} ${byId[id].last_name}` : t('family.unknownProfessional'));
    const treatmentsOf = (dependentId) => (treatments.data ?? [])
      .filter((row) => row.dependent_id === dependentId)
      .map((row) => ({
        ...row,
        professional: named(row.therapist_id),
        referrer: row.requested_via === 'referral' && row.referred_by_therapist_id ? named(row.referred_by_therapist_id) : null,
      }));
    setDependents((deps.data ?? []).map((row) => ({ ...row, treatments: treatmentsOf(row.id) })));
    setAppointments((appts.data ?? []).map((row) => ({
      ...row,
      dependents: { first_name: row.first_name, last_name: row.last_name },
      therapists: byId[row.therapist_id] ?? null,
    })));
    setOwed((balance.data ?? []).filter((row) => row.shown && Number(row.owed_total) > 0));
    setError(deps.error?.message ?? treatments.error?.message ?? appts.error?.message
      ?? balance.error?.message ?? null);
    setLoading(false);
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  // Only someone no professional has accepted: once one has, they have history,
  // which is kept (the database refuses deleting it).
  const removeMember = async (dependent) => {
    if (!window.confirm(t('family.confirmRemove', { name: `${dependent.first_name} ${dependent.last_name}` }))) return;
    setError(null);
    const { error: deleteError } = await supabase.from('dependents').delete().eq('id', dependent.id);
    if (deleteError) { setError(deleteError.message); return; }
    load();
  };

  const withdraw = async (treatment) => {
    if (!window.confirm(t('family.confirmWithdraw', { name: treatment.professional }))) return;
    setError(null);
    const { error: withdrawError } = await supabase.rpc('withdraw_treatment_request', { p_treatment_id: treatment.id });
    if (withdrawError) { setError(withdrawError.message); return; }
    load();
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6">
      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {/* Above the family: what is owed is the one thing on this page to act
          on. Only professionals who show balances in the app appear (0046). */}
      {owed.length > 0 && (
        <Alert tone="warning" title={t('home.owesTitle')}>
          <ul className="space-y-0.5">
            {owed.map((row) => (
              <li key={`${row.dependent_id}-${row.therapist_id}`}>
                {t(dependents.length > 1 ? 'home.owesLineFor' : 'home.owesLine', {
                  person: row.first_name,
                  amount: formatMoney(row.owed_total),
                  professional: row.professional,
                })}
              </li>
            ))}
          </ul>
          <Link to="/mis-pagos" className="mt-3 inline-block">
            <Button variant="secondary"><Wallet size={16} />{t('home.seePayments')}</Button>
          </Link>
        </Alert>
      )}

      {/* Forms a professional asked them to answer (0065). */}
      <PendingForms showPerson={dependents.length > 1} />

      {/* Adding a minor opens from the header, beside the title, instead of a
          button under the list that made the card longer (15 Sep 2026). */}
      <Card
        title={t('patients.dependents')}
        subtitle={t('home.patientSubtitle')}
        actions={!addingMinor && (
          <Button variant="secondary" onClick={() => setAddingMinor(true)}>
            <UserPlus size={16} />{t('family.addMinor')}
          </Button>
        )}
      >
        {addingMinor && (
          <div className="mb-4">
            <AddMinor accountId={accountId} onAdded={load} onClose={() => setAddingMinor(false)} />
          </div>
        )}
        {dependents.length === 0 ? (
          <EmptyState icon={Users} title={t('patients.noDependents')} />
        ) : (
          <ul className="space-y-3">
            {dependents.map((dependent) => {
              const accepted = dependent.treatments.some((row) => row.status === 'approved' || row.status === 'revoked');
              const taken = new Set(dependent.treatments
                .filter((row) => row.status === 'approved' || row.status === 'pending')
                .map((row) => row.therapist_id));
              return (
                <li key={dependent.id} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900">
                          {dependent.first_name} {dependent.last_name}
                        </p>
                        <Badge tone={dependent.relationship_to_account === 'self' ? 'info' : 'neutral'}>
                          {dependent.relationship_to_account === 'self'
                            ? t('patients.self')
                            : dependent.relationship_to_account}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500">
                        {dependent.insurance_plans
                          ? `${dependent.insurance_plans.insurance_companies?.name} · ${dependent.insurance_plans.name}`
                          : t('insurance.particular')}
                      </p>

                      {dependent.treatments.length === 0 ? (
                        <p className="mt-2 text-sm text-slate-600">{t('patients.noProfessional')}</p>
                      ) : (
                        <ul className="mt-2 space-y-1.5">
                          {dependent.treatments.map((treatment) => {
                            const [tone, Icon, label] = TREATMENT_BADGE[treatment.status];
                            return (
                              <li key={treatment.id} className="flex flex-wrap items-center gap-2 text-sm">
                                <span className="text-slate-800">{treatment.professional}</span>
                                <Badge tone={tone}>{Icon && <Icon size={12} />} {t(label)}</Badge>
                                {treatment.status === 'pending' && treatment.referrer && (
                                  <span className="text-xs text-slate-500">{t('family.referredBy', { name: treatment.referrer })}</span>
                                )}
                                {treatment.status === 'declined' && treatment.decline_reason && (
                                  <span className="text-xs text-slate-500">{t('family.declineReason', { reason: treatment.decline_reason })}</span>
                                )}
                                {treatment.status === 'pending' && (
                                  <button onClick={() => withdraw(treatment)}
                                    className="text-xs font-medium text-slate-500 hover:text-rose-600 hover:underline">
                                    {t('family.withdraw')}
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}

                      <RequestProfessional
                        dependent={dependent}
                        taken={taken}
                        onRequested={(message) => { setNotice(message); load(); }}
                      />
                    </div>
                    {dependent.relationship_to_account !== 'self' && !accepted && (
                      <Button variant="ghost" onClick={() => removeMember(dependent)} title={t('family.remove')}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card
        title={t('appointments.upcoming')}
        subtitle={appointments.some((appointment) => patientMayCancel(appointment)) ? t('patientCancel.listHint') : undefined}
      >
        {appointments.length === 0 ? (
          <EmptyState icon={CalendarDays} title={t('appointments.noUpcoming')} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {appointments.map((appointment) => {
              const details = (
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    {appointment.dependents?.first_name} {appointment.dependents?.last_name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatDate(appointment.starts_at)} · {formatTime(appointment.starts_at)}
                    {appointment.therapists && ` · ${appointment.therapists.first_name} ${appointment.therapists.last_name}`}
                  </p>
                </div>
              );
              // A request that was not approved says what became of it rather
              // than "Cancelado" (0055).
              const badge = appointment.status === 'cancelled'
                && ['declined', 'lapsed', 'withdrawn'].includes(appointment.request_status) ? (
                  <Badge tone="neutral">{t(`appointments.requestOutcome.${appointment.request_status}`)}</Badge>
                ) : (
                  <Badge tone={STATUS_TONE[appointment.status]}>
                    {t(`appointments.status.${appointment.status}`)}
                  </Badge>
                );
              return (
                <li key={appointment.id}>
                  {/* The whole row opens the cancel dialog (0057). No separate
                      button beside it, as asked on 15 Sep 2026: the card's
                      subtitle says to press a session. */}
                  {patientMayCancel(appointment) ? (
                    <button
                      type="button"
                      onClick={() => setCancelling(appointment)}
                      title={t(appointment.status === 'requested' ? 'patientCancel.rowWithdraw' : 'patientCancel.rowCancel')}
                      className="flex w-full items-center justify-between gap-4 rounded-lg px-2 py-3 text-left transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {details}
                      {badge}
                    </button>
                  ) : (
                    <div className="flex items-center justify-between gap-4 px-2 py-3">
                      {details}
                      {badge}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {cancelling && (
        <PatientCancelModal
          session={cancelling}
          professionalName={cancelling.therapists
            ? `${cancelling.therapists.first_name} ${cancelling.therapists.last_name}`
            : t('booking.yourProfessional')}
          onClose={() => setCancelling(null)}
          onDone={(message) => { setCancelling(null); setNotice(message); load(); }}
        />
      )}
    </div>
  );
};

const HomeView = () => {
  const { profile, isPatient, isAdmin, therapistId, accountId } = useSession();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          {t('home.greeting', { name: profile?.first_name ?? '' })}
        </h1>
        <p className="text-sm text-slate-500">{formatDate(new Date(), { weekday: 'long', day: 'numeric', month: 'long' })}</p>
      </div>

      {isPatient
        ? <PatientHome accountId={accountId} />
        : <ProfessionalHome therapistId={therapistId} isAdmin={isAdmin} />}
    </div>
  );
};

export default HomeView;
